/*
 * Preventative Maintenance Table
 */

import * as React from 'react';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IWorkOrder, IjobWorkOrder, ItableFiltersReducer, Itile, Iuser, Ijob } from '../../models';
import { useTable, useSortBy, usePagination, useRowSelect, useFlexLayout, Column } from 'react-table';
import find from 'lodash/find';
import { setTableFilter } from '../../actions/workOrderActions';
import { TFunction } from 'react-i18next';
import { TableUtil } from '../common/TableUtil';
import { constants } from '../../constants/constants';
import ConfirmSelectJobModal from '../workOrder/ConfirmSelectJobModal';
import { Button } from 'react-bootstrap';
import { emptyTile } from '../../reducers/initialState';
import { RouteComponentProps } from 'react-router-dom';
import { WithTranslation } from 'react-i18next';
import { workOrderTypesEnum } from '../../models-enums';

interface Iprops extends RouteComponentProps<any> {
    tableData: IWorkOrder[];
    t: TFunction;
    setSelectedWorkOrderID: (id: string) => void;
    loading: boolean;
    tableFilters: ItableFiltersReducer;
    setTableFilter: typeof setTableFilter;
    user: Iuser;
    selection: string[];
    updateWorkOrderSelection: (selection: string[]) => void;
    jobWorkOrdersByID: { [key: string]: IjobWorkOrder };
    jobsByID: { [key: string]: Ijob };
    selectedJob: Ijob;
    preventativeMaintenanceEnabled?: boolean;
}

const PreventativeMaintenanceTableWithSelect: React.FC<Iprops & WithTranslation> = props => {
    const {
        tableData,
        t,
        jobWorkOrdersByID,
        selectedJob,
        location,
        updateWorkOrderSelection,
        setSelectedWorkOrderID
        // preventativeMaintenanceEnabled
    } = props;

    const [selectedRow, setSelectedRow] = useState<any>({});
    const [currentTile, setCurrentTile] = useState<Itile>(emptyTile);
    const lastSelectedIdsRef = useRef<string>('');
    const updateSelectionRef = useRef(updateWorkOrderSelection);

    // Pagination state
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(constants.tablePageSizeDefault || 25);

    // Update the ref when the function changes
    useEffect(() => {
        updateSelectionRef.current = updateWorkOrderSelection;
    }, [updateWorkOrderSelection]);

    // Build columns using useMemo for performance
    const columns = useMemo(() => {
        const selectionColumn = {
            Header: ({ state, toggleAllRowsSelected }: any) => {
                const { selectedRowIds } = state;
                const checkedCount = selectedRowIds ? Object.keys(selectedRowIds).length : 0;
                const isIndeterminate = checkedCount > 0 && checkedCount < tableData.length;
                const isChecked = checkedCount === tableData.length && tableData.length > 0;

                return (
                    <div className="d-flex justify-content-center">
                        <input
                            type="checkbox"
                            ref={input => {
                                if (input) input.indeterminate = isIndeterminate;
                            }}
                            checked={isChecked}
                            onChange={e => toggleAllRowsSelected(e.target.checked)}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                );
            },
            id: 'selection',
            accessor: '_selector',
            width: 50,
            minWidth: 50,
            maxWidth: 50,
            Cell: ({ row, state, toggleRowSelected }: any) => {
                const { selectedRowIds } = state;
                const rowData = row.original;

                return (
                    <div className="d-flex justify-content-center">
                        <input
                            type="checkbox"
                            checked={selectedRowIds[rowData.id] || false}
                            onChange={e => {
                                toggleRowSelected(rowData.id, e.target.checked);
                            }}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                );
            }
        };

        const dataColumns = TableUtil.translateHeaders(
            [
                {
                    Header: 'Activity',
                    accessor: 'activityDescription',
                    width: 150,
                    minWidth: 150
                },
                {
                    Header: 'manageWorkOrder:productName',
                    accessor: 'productName',
                    width: 300,
                    minWidth: 180
                },
                {
                    Header: 'Location',
                    accessor: 'installBaseLocation',
                    width: 250,
                    minWidth: 160
                }
            ],
            t
        );

        return [selectionColumn, ...dataColumns] as Column<IWorkOrder>[];
    }, [t, tableData.length]);

    // Use react-table v7 hooks with sorting, pagination, and row selection
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        pageCount,
        gotoPage,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        setPageSize: tableSetPageSize,
        state: { pageIndex: currentPageIndex, selectedRowIds }
    } = useTable<IWorkOrder>(
        {
            columns,
            data: tableData,
            initialState: {
                pageIndex,
                pageSize
            },
            getRowId: (row: IWorkOrder) => row.id,
            autoResetSelectedRows: false
        } as any,
        useSortBy,
        usePagination,
        useRowSelect,
        useFlexLayout
    ) as any;

    const handleAddedWorkOrders = useCallback(() => {
        updateSelectionRef.current([]);
    }, []);

    // Component did mount equivalent
    useEffect(() => {
        document.addEventListener('addedWorkOrdersToJob', handleAddedWorkOrders, false);
        updateSelectionRef.current([]);
        setCurrentTile(constants.getTileByURL(location.pathname));

        return () => {
            document.removeEventListener('addedWorkOrdersToJob', handleAddedWorkOrders, false);
        };
    }, [location.pathname, handleAddedWorkOrders]);

    // Sync react-table selection with Redux state
    useEffect(() => {
        const selectedIds = Object.keys(selectedRowIds);
        const selectedIdsString = selectedIds.sort().join(',');

        // Only update if the selection has actually changed
        if (selectedIdsString !== lastSelectedIdsRef.current) {
            lastSelectedIdsRef.current = selectedIdsString;
            updateSelectionRef.current(selectedIds);
        }
    }, [selectedRowIds]);

    // Handle row click
    const getTrProps = (rowInfo: any) => {
        if (rowInfo) {
            const hasJobWorkOrder = find(jobWorkOrdersByID, {
                workOrderID: rowInfo.original.id
            });
            return {
                style: {
                    background:
                        rowInfo.index === selectedRow
                            ? constants.colors[`${currentTile.color}Tr` as keyof typeof constants.colors]
                            : '',
                    color: hasJobWorkOrder ? constants.colors.blue : 'inherit'
                },
                onClick: () => {
                    setSelectedWorkOrderID(rowInfo.original.id);
                    setSelectedRow({
                        [rowInfo.viewIndex || 0]: !selectedRow[rowInfo.viewIndex || 0]
                    });
                }
            };
        } else {
            return {};
        }
    };

    const colorButton = constants.colors[`${currentTile.color}Button` as keyof typeof constants.colors];

    const selectedCount = Object.keys(selectedRowIds).length;
    let tableClassName = `ReactTable beacon-table preventative-maintenance -highlight ${
        currentTile.color
    } flex-layout ${selectedCount > 0 ? 'with-action-button' : ''}`;

    return (
        <>
            {/* Table */}
            <div {...getTableProps()} className={tableClassName}>
                <div className="rt-thead -header sticky-header">
                    {headerGroups.map((headerGroup: any) => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                            className="rt-tr"
                        >
                            {headerGroup.headers.map((column: any) => {
                                const isSorted = column.isSorted;
                                const isSortedDesc = column.isSortedDesc;
                                return (
                                    <div
                                        {...column.getHeaderProps({
                                            className: `rt-th ${
                                                column.id !== 'selection' && isSorted
                                                    ? isSortedDesc
                                                        ? '-sort-desc'
                                                        : '-sort-asc'
                                                    : ''
                                            }`
                                        })}
                                        key={column.getHeaderProps().key}
                                        onClick={() => {
                                            if (column.id !== 'selection') {
                                                column.toggleSortBy();
                                            }
                                        }}
                                    >
                                        <div>{column.render('Header')}</div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div {...getTableBodyProps()} className="rt-tbody">
                    {page.map((row: any, i: number) => {
                        prepareRow(row);
                        const rowProps = getTrProps({ index: i, original: row.original, viewIndex: i });
                        const isEvenRow = i % 2 === 0;
                        return (
                            <div
                                {...row.getRowProps()}
                                key={row.getRowProps().key}
                                className={`rt-tr ${isEvenRow ? '-even' : '-odd'}`}
                                onClick={rowProps.onClick}
                                style={rowProps.style}
                            >
                                {row.cells.map((cell: any) => {
                                    return (
                                        <div {...cell.getCellProps()} key={cell.getCellProps().key} className="rt-td">
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Pagination Controls */}
            <div className="table-pagination">
                <Button
                    onClick={() => {
                        previousPage();
                        setPageIndex(Math.max(0, currentPageIndex - 1));
                    }}
                    variant="outline"
                    className="prev"
                    disabled={!canPreviousPage || props.loading}
                >
                    {t('common:previous')}
                </Button>

                <div className="controls">
                    <label htmlFor="page-input">{t('common:pageText')} </label>
                    <span>
                        <input
                            id="page-input"
                            type="number"
                            min={1}
                            max={pageCount}
                            className="page-input"
                            defaultValue={currentPageIndex + 1}
                            key={`page-input-${currentPageIndex}`}
                            disabled={props.loading}
                            onChange={e => {
                                const gotoPageNum = e.target.value ? Number(e.target.value) - 1 : 0;
                                if (gotoPageNum >= 0 && gotoPageNum < pageCount) {
                                    gotoPage(gotoPageNum);
                                    setPageIndex(gotoPageNum);
                                }
                            }}
                            onWheel={e => e.currentTarget.blur()}
                        />
                    </span>
                    <span>{t('common:of')} </span>
                    <span>{pageCount}</span>

                    <select
                        value={pageSize}
                        disabled={props.loading}
                        onChange={e => {
                            const newPageSize = Number(e.target.value);
                            tableSetPageSize(newPageSize);
                            setPageSize(newPageSize);
                            setPageIndex(0);
                            gotoPage(0);
                        }}
                    >
                        {constants.tablePageSizeOptions.map(size => (
                            <option key={size} value={size}>
                                {t('common:show')} {size}
                            </option>
                        ))}
                    </select>
                </div>

                <Button
                    onClick={() => {
                        nextPage();
                        setPageIndex(Math.min(pageCount - 1, currentPageIndex + 1));
                    }}
                    variant="outline"
                    className="next"
                    disabled={!canNextPage || props.loading}
                >
                    {t('common:next')}
                </Button>
            </div>

            <ConfirmSelectJobModal
                colorButton={colorButton}
                facilityID={selectedJob.facilityID}
                workOrderMode={workOrderTypesEnum.pmp}
                history={props.history}
                location={location}
                match={props.match}
                forSAP={false}
            />
        </>
    );
};

export default PreventativeMaintenanceTableWithSelect;
